import { computed, ref } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'

export default function useContentPacks() {
  const RESOURCE = '/content-pack'
  const { showSnackbar } = useSnackbar()

  const dtHeaders = computed(() => ([
    { text: 'DISPLAY NAME', value: 'displayName', sortable: true },
    {
      text: 'SKU', value: 'sku', sortable: true, width: 140,
    },
    {
      text: 'FITNESS', value: 'fitnessIdsCount', sortable: true, width: 180,
    },
    {
      text: 'PRACTICE', value: 'practiceIdsCount', sortable: true, width: 180,
    },
    {
      text: 'THEFLOW', value: 'flowIdsCount', sortable: true, width: 180,
    },
    {
      text: 'ACADEMIES', value: 'academiesDisplay', sortable: true, width: 220,
    },
    {
      text: 'CREATOR', value: 'creator', sortable: true, width: 180,
    },
    {
      text: 'ACTIVE', value: 'active', sortable: true, width: 120, class: 'compact-table-column', cellClass: 'compact-table-column',
    },
    {
      text: 'ACTIONS', value: 'actions', sortable: false, width: 140, align: 'center', class: 'compact-table-column', cellClass: 'compact-table-column',
    },
  ]))

  const dtSearchQuery = ref('')
  const contentPacks = ref([])
  const isLoading = ref(false)

  const blankContentPack = {
    id: '',
    name: '',
    displayName: '',
    sku: '',
    description: '',
    fitness: {
      exercises: [],
      sessions: [],
      assessments: [],
    },
    practice: {
      exercises: [],
      sessions: [],
      assessments: [],
    },
    flow: {
      exercises: [],
      sessions: [],
      assessments: [],
    },
    plans: [],
    fitnessIdsCount: 0,
    fitnessExercisesIdsCount: 0,
    fitnessSessionsIdsCount: 0,
    fitnessAssessmentsIdsCount: 0,
    practiceIdsCount: 0,
    practiceExercisesIdsCount: 0,
    practiceSessionsIdsCount: 0,
    practiceAssessmentsIdsCount: 0,
    flowIdsCount: 0,
    flowExercisesIdsCount: 0,
    flowSessionsIdsCount: 0,
    flowAssessmentsIdsCount: 0,
    active: true,
    deleted: false,
    academies: [],
  }

  const normalizeResponse = data => {
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.contentPacks)) return data.contentPacks
    if (Array.isArray(data?.items)) return data.items
    if (Array.isArray(data?.data)) return data.data

    return []
  }

  const fetchContentPacks = async () => {
    isLoading.value = true

    try {
      const response = await axios.get(RESOURCE, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      contentPacks.value = normalizeResponse(response.data)
        .map(item => ({
          ...item,
          fitness: item.fitness || structuredClone(blankContentPack.fitness),
          practice: item.practice || structuredClone(blankContentPack.practice),
          flow: item.flow || structuredClone(blankContentPack.flow),
          academies: Array.isArray(item.academies) ? item.academies : [],
          academiesDisplay: Array.isArray(item.academies) ? item.academies.map(academy => academy.name).join('\n') : '',

          // Backend may still return Mongo's raw _id field.
          // eslint-disable-next-line no-underscore-dangle
          id: item.id || item._id,
        }))
        .filter(item => !item.deleted)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to fetch content packs:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const createContentPack = async contentPack => {
    isLoading.value = true

    try {
      await axios.post(RESOURCE, contentPack, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      showSnackbar('Content pack created successfully', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to create content pack:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const updateContentPack = async contentPack => {
    isLoading.value = true

    try {
      await axios.put(`${RESOURCE}/${contentPack.id}`, contentPack, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      showSnackbar('Content pack updated successfully', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to update content pack:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const deleteContentPack = async contentPack => {
    isLoading.value = true

    try {
      await axios.put(`${RESOURCE}/${contentPack.id}`, {
        ...contentPack,
        deleted: true,
      }, {
        headers: {
          'Content-Type': 'application/vnd.p72.app-1.0+json',
        },
      })

      showSnackbar('Content pack deleted successfully', SnackbarType.SUCCESS)

      return true
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      const statusCode = error.response?.status ?? null

      showSnackbar(
        `Failed to delete content pack:<br> ${statusCode ? `[${statusCode}]` : ''} ${message}`,
        SnackbarType.ERROR,
      )

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetState = () => {
    contentPacks.value = []
    dtSearchQuery.value = ''
    isLoading.value = false
  }

  return {
    dtHeaders,
    dtSearchQuery,
    contentPacks,
    blankContentPack,
    isLoading,
    fetchContentPacks,
    createContentPack,
    updateContentPack,
    deleteContentPack,
    resetState,
  }
}
